import { isStandalone } from "./registerSW.js";

const ASSETS_CACHE = "assets-cache-v1.0.1";

/**
 * Cache assets (images and audios) pour utilisation hors ligne en mode PWA
 * @param {Array} assets - Tableau de {uuid, url, extension, urlCachePWA, type}
 * @param {Function} onProgress - Callback progression (current, total)
 * @returns {Promise<{success: number, failed: number, errors: Array}>}
 */
export async function cacheAssets(assets, onProgress = null) {
    // Seulement en mode PWA
    if (!isStandalone()) {
        console.log("Not in PWA mode, skipping asset caching");
        return { success: 0, failed: 0, errors: [] };
    }

    if (!('caches' in window)) {
        console.warn("Cache API not available");
        return { success: 0, failed: 0, errors: [] };
    }

    const assetsToCache = assets.filter(asset => asset.urlCachePWA && asset.url);

    if (assetsToCache.length === 0) {
        return { success: 0, failed: 0, errors: [] };
    }

    let successCount = 0;
    let failedCount = 0;
    const errors = [];

    try {
        const cache = await caches.open(ASSETS_CACHE);

        for (let i = 0; i < assetsToCache.length; i++) {
            const asset = assetsToCache[i];

            try {
                // Fetch depuis l'URL temporaire
                const response = await fetch(asset.url);

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                // Cache avec l'URL virtuelle PWA
                await cache.put(asset.urlCachePWA, response);

                successCount++;
                if (onProgress) {
                    onProgress(i + 1, assetsToCache.length);
                }
            } catch (error) {
                console.error(`Failed to cache ${asset.type || 'asset'} ${asset.uuid}:`, error);
                failedCount++;
                errors.push({ uuid: asset.uuid, type: asset.type, error: error.message });
            }
        }

        console.log(`Asset caching: ${successCount} success, ${failedCount} failed`);
    } catch (error) {
        console.error("Failed to open cache:", error);
        return { success: 0, failed: assetsToCache.length, errors: [{ error: error.message }] };
    }

    return { success: successCount, failed: failedCount, errors };
}

/**
 * Clear the assets cache
 */
export async function clearAssetCache() {
    if ('caches' in window) {
        await caches.delete(ASSETS_CACHE);
        console.log("Asset cache cleared");
    }
}
